import { IconArrowLeft, IconArrowRight, useDirection } from '@veneer/core';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDependencyManagerContext } from 'src/contexts/dependencyManager';
import {
  Loading,
  LookingForSomethingElse,
  publishEvent,
  RenderCardProps,
  OfferManager,
  OfferCardsRenderer,
  useHpIdCountry
} from '@uma/react-commons/dist/index';
import DeviceDetails from 'src/components/DeviceDetails';
import MoreOptions from 'src/components/MoreOptions';
import Subscriptions from 'src/components/Subscriptions';
import { DeviceDetailsError } from 'src/components/Subscriptions/analytics';
import UsersAndSharing from 'src/components/UsersAndSharing';
import GET_DEVICE_DETAILS_SUBSCRIPTIONS from 'src/graphql/getDeviceDetailsSubscriptions';
import GET_DEVICE_DETAILS from 'src/graphql/getDeviceDetails';
import { usePathname } from 'src/hooks/usePathname/usePathname';
import { ContainerContent, Section } from 'src/styles/styles';
import { screenPathDeviceDetailing } from 'src/utils/constants';
import { ScreenName } from 'src/utils/enums';
import { DeviceDetailsHeader, Title } from './styles';
import { DeviceDetailingFeatureFlagProps } from 'src/App';
import { buildSubscriptionsPayload } from 'src/utils/buildSubscriptionsPayload';
import OfferCard from 'src/components/OfferCard';

const DeviceDetailing = ({
  isLoading,
  originalFeedbackExperience,
  useMethoneCacheApiDeviceSupport,
  showOfferCardDeviceDetails,
  showPriceDeviceDetailsOffer
}: DeviceDetailingFeatureFlagProps): JSX.Element => {
  const { translate, northboundAPIs } = useDependencyManagerContext();
  const deviceId = usePathname(true);

  const { useQuery } = northboundAPIs.v1.graphql.reactTools;

  const { data, loading, error } = useQuery(GET_DEVICE_DETAILS, {
    variables: {
      deviceId
    }
  });

  const { country: hpIdCountry, loading: hpIdCountryLoading } =
    useHpIdCountry();

  const subscriptionsData = useQuery(GET_DEVICE_DETAILS_SUBSCRIPTIONS);

  const navigation = northboundAPIs.v1.navigation;
  const country = northboundAPIs.v1.localization.country;
  const language = northboundAPIs.v1.localization.language;

  const { isRTL } = useDirection();
  const IconArrow = isRTL ? IconArrowRight : IconArrowLeft;

  const showOriginalFeedbackExperience =
    originalFeedbackExperience && !isLoading;
  const isToUseMethoneCacheApiDeviceSupport =
    useMethoneCacheApiDeviceSupport && !isLoading;

  const goBack = useCallback(
    () => navigation.push(`/${country}/${language}/devices`),
    [country, language, navigation]
  );

  useEffect(() => {
    if (error) {
      publishEvent(DeviceDetailsError(error?.message));
      goBack();
    }

    if (!loading && !error) {
      const deviceExists = data?.devices?.items[0]?.deviceId === deviceId;

      if (!deviceExists || !data?.devices?.totalCount) {
        goBack();
      }
    }
  }, [
    data?.devices?.items,
    data?.devices?.totalCount,
    deviceId,
    error,
    goBack,
    loading
  ]);

  const device = data?.devices?.items[0];

  const deviceDetailsAnalytics = `deviceType=${device?.type?.toLowerCase()}&productNumber=${device?.identity?.makeAndModel?.number}`;

  const showUsersAndSharing = device?.type === 'PRINTER';

  const subscriptions = useMemo(
    () => buildSubscriptionsPayload(device, subscriptionsData),
    [device, subscriptionsData]
  );

  const renderOfferCard = useCallback(
    ({ offers, onClick }: RenderCardProps) => (
      <OfferCardsRenderer
        offers={offers}
        onClick={onClick}
        OfferCardComponent={OfferCard}
        offerCardProps={{
          country: hpIdCountry,
          isRtl: isRTL,
          showPriceDeviceDetailsOffer
        }}
      />
    ),
    [hpIdCountry, isRTL, showPriceDeviceDetailsOffer]
  );

  return (
    <>
      <DeviceDetailsHeader data-testid="DeviceDetailsHeader">
        <IconArrow
          data-testid={isRTL ? 'IconArrowRight' : 'IconArrowLeft'}
          onClick={goBack}
          tabIndex={0}
          aria-label={`${translate('common.goBack', 'Go back')}`}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              goBack();
            }
          }}
        />
        <Title>
          {`${translate('devices.deviceDetails', 'Device details')}`}
        </Title>
      </DeviceDetailsHeader>
      {loading ? (
        <Section
          background="filled"
          border="outlined"
        >
          <Loading data-testid="loading" />
        </Section>
      ) : (
        <ContainerContent>
          <DeviceDetails device={device} />
          <Subscriptions
            device={device}
            deviceDetails={deviceDetailsAnalytics}
            subscriptionsData={subscriptionsData}
          />
          {showUsersAndSharing && (
            <UsersAndSharing deviceDetails={deviceDetailsAnalytics} />
          )}
          <MoreOptions
            deviceDetails={deviceDetailsAnalytics}
            device={device}
            useMethoneCacheApiDeviceSupport={
              isToUseMethoneCacheApiDeviceSupport
            }
          />

          {showOfferCardDeviceDetails && !hpIdCountryLoading && (
            <OfferManager
              showOfferCard={showOfferCardDeviceDetails}
              modelNumber={device?.identity?.makeAndModel?.number}
              serialNumber={device?.identity?.serialNumber}
              subscriptions={subscriptions}
              renderCard={renderOfferCard}
            />
          )}
        </ContainerContent>
      )}
      {showOriginalFeedbackExperience && (
        <LookingForSomethingElse
          analytics={{
            screenName: ScreenName.DEVICE_DETAILS,
            screenPath: screenPathDeviceDetailing
          }}
        />
      )}
    </>
  );
};

export default DeviceDetailing;
